import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Link from '@amzn/meridian/link';
import Divider from '@amzn/meridian/divider';
import './LandingPage.css';

const MODULE_DESCRIPTIONS = {
  MtUmet: 'Test translations in real time. Enter text, select source and target languages, and compare Luma (LLM) vs MTS output.',
  TranslationResources: 'Manage machine translation engines, translation memories, terminology bases, and AWS accounts per tenant.',
  TerminologyData: 'Add, edit, and bulk-upload terminology entries to ensure consistent translations across language pairs.',
  Workflows: 'Configure translation workflows — define routing rules, language arcs, and A/B experiment settings.',
  UserManager: 'Manage user access and roles for each tenant.',
  TenantLookup: 'Look up and manage tenant settings and onboarding configuration.',
};

const quickStartSteps = [
  { step: '1', text: 'Select Debug Tool from the sidebar menu.' },
  { step: '2', text: 'Enter text in the Source Text field.' },
  { step: '3', text: 'Choose source and target languages (ensure correct locale, e.g. nl-NL vs nl-BE).' },
  { step: '4', text: 'Select Luma from the Translation Type dropdown.' },
  { step: '5', text: 'Check the Workflow Type in the response — LLM means Luma, MTS means legacy Rosetta.' },
];

const LandingPage = ({ modules = [] }) => {
  const featureModules = modules.filter(m => MODULE_DESCRIPTIONS[m.name]);
  const debugModule = modules.find(m => m.name === 'MtUmet');

  return (
    <Column spacing="large" spacingInset="large" maxWidth="900px" className="landing-page">
      <Column spacing="small">
        <Heading level={2}>Welcome to Luminous App</Heading>
        <Text type="b300" color="secondary">
          Translation management and debugging tool for Luma Translation Service / Machine Translation Service.
        </Text>
      </Column>

      <Divider />

      <Column spacing="medium">
        <Heading level={4}>Quick Start — Test a Translation</Heading>
        <Column spacing="xsmall" className="quick-start-card">
          {quickStartSteps.map(({ step, text }) => (
            <Row key={step} spacing="small" alignmentVertical="top">
              <span className="step-badge">{step}</span>
              <Text type="b200">{text}</Text>
            </Row>
          ))}
          <Row spacingInset="small none none none">
            {debugModule && (
              <Link href={`#${debugModule.routeProps.path}`} type="secondary">
                Open Debug Tool →
              </Link>
            )}
          </Row>
        </Column>
      </Column>

      <Divider />

      <Column spacing="medium">
        <Heading level={4}>Features</Heading>
        <div className="feature-grid">
          {featureModules.map(({ name, label, iconClassName, routeProps }) => (
            <Link key={name} href={`#${routeProps.path}`} className="feature-card-link">
              <Column spacing="xsmall" className="feature-card">
                <Row spacing="small" alignmentVertical="center">
                  <i className={`${iconClassName} feature-icon`} />
                  <Text type="h100">{label}</Text>
                </Row>
                <Text type="b200" color="secondary">{MODULE_DESCRIPTIONS[name]}</Text>
              </Column>
            </Link>
          ))}
        </div>
      </Column>

      <Divider />

      <Column spacing="xsmall">
        <Heading level={4}>Resources</Heading>
        <Row spacing="large">
          <Link
            href="https://wiki.amazon.com/bin/view/CS_Breeze/LuminousAppUsageSOP/"
            target="_blank"
            type="secondary"
          >
            Testing SOP
          </Link>
          <Link
            href="https://t.corp.amazon.com/create/templates/f26f085a-8399-4c03-b5f6-43b6f25a8347"
            target="_blank"
            type="secondary"
          >
            Request Access
          </Link>
        </Row>
      </Column>
    </Column>
  );
};

export default LandingPage;
